import React, { useContext, useEffect, useState } from 'react';
import { Gem, Clock, Users, Award, Sparkles, Heart, ShieldCheck, Star, PackageCheck, ThumbsUp } from 'lucide-react';
import { AppContext, DEFAULT_STATS } from './AppContext';
import './StatsSection.css';

const ICON_MAP = {
  Gem,
  Clock,
  Users,
  Award,
  Sparkles,
  Heart,
  ShieldCheck,
  Star,
  PackageCheck,
  ThumbsUp
};

// Animated Counter that counts up from 0 to target number on page load/refresh
const AnimatedCounter = ({ target, suffix = '', duration = 2000 }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let startTime = null;
    let animationFrame;

    // Small delay so user sees the counter start from 0 right as page loads
    const timer = setTimeout(() => {
      const animate = (timestamp) => {
        if (!startTime) startTime = timestamp;
        const elapsed = timestamp - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Smooth easeOutCubic curve
        const easeOut = 1 - Math.pow(1 - progress, 3);
        const currentVal = Math.floor(easeOut * target);

        setCount(currentVal);

        if (progress < 1) {
          animationFrame = requestAnimationFrame(animate);
        } else {
          setCount(target);
        }
      };

      animationFrame = requestAnimationFrame(animate);
    }, 150);

    return () => {
      clearTimeout(timer);
      if (animationFrame) cancelAnimationFrame(animationFrame);
    };
  }, [target, duration]);

  return (
    <div className="stat-number">
      {count}{suffix}
    </div>
  );
};

const StatsSection = () => {
  const { stats, lang } = useContext(AppContext);
  const isMr = lang === 'mr';
  const displayStats = stats && stats.length > 0 ? stats : DEFAULT_STATS;

  return (
    <section className="stats-section" id="stats">
      <div className="stats-bg" />
      <div className="stats-inner">
        {displayStats.map((s, i) => {
          const IconComp = ICON_MAP[s.icon] || Gem;
          const targetNum = typeof s.target === 'number' ? s.target : (parseInt(s.target, 10) || 0);
          const labelText = isMr && s.labelMr ? s.labelMr : s.label;

          return (
            <div key={s.id || i} className="stat-item">
              <div className="stat-icon">
                <IconComp size={30} />
              </div>
              <AnimatedCounter
                target={targetNum}
                suffix={s.suffix || ''}
                duration={2000}
              />
              <div className="stat-label">{labelText}</div>
              {i < displayStats.length - 1 && <div className="stat-divider" />}
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default StatsSection;
